import { CookieHandler } from '@/Telemetry/cookie-init';
import { useRequestPostAccess } from '@/graphql/mutations/Utils.mutation';
import type { InputMaybe, ZnglyPostTypeEnum } from '@/graphql/types';
import { Box, Button, Card, CardContent, Container, Modal, Typography } from '@mui/material';
import { unstable_OneTimePasswordField as OneTimePasswordField } from 'radix-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useTimer } from 'react-use-precision-timer';

type Props = {
  type: InputMaybe<ZnglyPostTypeEnum>;
  id: string;
  onSuccess: () => void;
};

export const RequestOTPAccess = (props: Props) => {
  const [step, setStep] = useState<'1' | '2'>('1');
  const [email, setEmail] = useState<string>('');

  return (
    <Modal open={true}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'rgba(255, 255, 255, .9)',
        }}
      >
        <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Card
            sx={{
              width: '100%',
              maxWidth: 420,
              mx: 'auto',
              borderRadius: 3,
              boxShadow: 6,
              p: 2,
              background: 'rgba(255,255,255,0.98)',
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              {step === '1' && <Step1 email={email} setEmail={setEmail} setStep={setStep} {...props} />}
              {step === '2' && <Step2 email={email} setEmail={setEmail} setStep={setStep} {...props} />}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Modal>
  );
};

type StepProps = {
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  setStep: React.Dispatch<React.SetStateAction<'1' | '2'>>;
} & Props;

function Step1(props: StepProps) {
  const [verify, isVerifyLoading] = useRequestPostAccess();
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (props.email && emailInputRef.current) {
      emailInputRef.current.value = props.email;
    }
  }, [props.email]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isVerifyLoading) {
      return;
    }

    const emailValue = emailInputRef.current?.value || e.currentTarget.email.value;
    props.setEmail(emailValue);

    verify(
      {
        input: {
          email: emailValue,
          id: props.id,
          idType: 'SLUG',
          postType: props.type,
        },
      },
      {
        onCompleted(response, errors) {
          if (!!errors?.length) {
            toast.error(errors?.map((e) => e.message).join(', '));
            return;
          }

          toast.info('Verification email sent! Please check your inbox.');
          props.setStep('2');
        },
      }
    );
  };

  return (
    <form
      onSubmit={onSubmit}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        maxWidth: '340px',
        margin: '0 auto',
      }}
    >
      <Typography variant="h5" component="h2" gutterBottom align="center" fontWeight={600}>
        Verify your email
      </Typography>
      <Typography variant="body1" gutterBottom align="center" color="text.secondary">
        Enter your email address and we'll send you a verification code.
      </Typography>
      <input
        ref={emailInputRef}
        type="email"
        placeholder="Enter your email"
        required
        name="email"
        style={{
          padding: '12px',
          borderRadius: '6px',
          border: '1px solid #d1d5db',
          fontSize: '16px',
          outline: 'none',
        }}
      />
      <Button type="submit" variant="contained" disabled={isVerifyLoading} sx={{ py: 1.5, fontWeight: 600 }}>
        {isVerifyLoading ? 'Sending Code...' : 'Send Verification Code'}
      </Button>
    </form>
  );
}

const OTP_LENGTH = 6;

function Step2(props: StepProps) {
  const [verify, isVerifyLoading] = useRequestPostAccess();
  const [expired, setExpired] = useState(false);
  const DURATION = 5 * 60 * 1000; // 5 minutes in ms
  const [remaining, setRemaining] = useState(DURATION);

  const timer = useTimer({ delay: 100, startImmediately: false }, () => {
    const elapsed = timer.getElapsedRunningTime();
    const left = Math.max(DURATION - elapsed, 0);
    setRemaining(left);
    if (left === 0) {
      setExpired(true);
      timer.stop();
    }
  });

  useEffect(() => {
    setExpired(false);
    setRemaining(DURATION);
    timer.start();
    return () => timer.stop();
  }, [props.email]);

  const onBack = () => {
    props.setStep('1');
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isVerifyLoading || expired) {
      return;
    }

    const otp = e.currentTarget.otp.value;

    verify(
      {
        input: {
          email: props.email,
          code: otp,
          id: props.id,
          idType: 'SLUG',
          postType: props.type,
          zncid: CookieHandler.getZnClientId(),
        },
      },
      {
        onCompleted(response, errors) {
          if (!!errors?.length) {
            toast.error(`Error verifying Code. Please try again. ${errors?.map((e) => e.message).join(', ')}`);
            return;
          }

          toast.success('Access granted!');
          props.onSuccess();
        },
      }
    );
  };

  const hiddenEmail = useMemo(() => {
    const token = props.email.split('@');
    if (token.length !== 2) return props.email; // Invalid email format, return as is
    const [localPart, domain] = token;
    const hiddenLocalPart =
      localPart.length > 2
        ? `${localPart[0]}${Array.from({ length: localPart.length - 2 }, () => '*').join('')}${localPart.slice(-1)}`
        : localPart;
    return `${hiddenLocalPart}@${domain}`;
  }, [props.email]);

  useEffect(() => {
    if (!props.email) {
      props.setStep('1');
      return;
    }
  }, [props.email]);

  return (
    <form
      onSubmit={onSubmit}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        maxWidth: '340px',
        margin: '0 auto',
      }}
    >
      <Typography variant="h5" component="h2" gutterBottom align="center" fontWeight={600}>
        Check your email
      </Typography>
      <Typography variant="body1" gutterBottom align="center" color="text.secondary">
        We sent a 6-digit verification code to <strong>{hiddenEmail}</strong>.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <OneTimePasswordField.Root
          name="otp"
          autoFocus
          validationType="alphanumeric"
          sanitizeValue={(value) => value.toUpperCase().slice(0, OTP_LENGTH)}
          style={{ display: 'flex', gap: '12px' }}
        >
          {Array.from({ length: OTP_LENGTH }, (_, i) => (
            <OneTimePasswordField.Input
              maxLength={1}
              // biome-ignore lint/suspicious/noArrayIndexKey: Using index as key is acceptable here for static OTP fields
              key={i}
              style={{
                textTransform: 'uppercase',
                width: '48px',
                height: '48px',
                textAlign: 'center',
                fontSize: '28px',
                border: '2px solid #bdbdbd',
                borderRadius: '8px',
                background: '#fafbfc',
                outline: 'none',
                transition: 'border 0.2s',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
              }}
              // Add focus style
              onFocus={(e) => e.currentTarget.style.setProperty('border', '2px solid #1976d2')}
              onBlur={(e) => e.currentTarget.style.setProperty('border', '2px solid #bdbdbd')}
            />
          ))}
          <OneTimePasswordField.HiddenInput maxLength={OTP_LENGTH} minLength={OTP_LENGTH} />
        </OneTimePasswordField.Root>
      </Box>
      <Button
        type="submit"
        variant="contained"
        sx={{ mt: 2, py: 1.5, fontWeight: 600 }}
        disabled={isVerifyLoading || expired}
      >
        <Typography
          variant="button"
          component="span"
          fontWeight={600}
          sx={{
            ...(expired && { display: 'none' }),
          }}
        >
          {isVerifyLoading ? 'Verifying...' : 'Verify Code'}
        </Typography>

        <Typography variant="button" fontWeight={600} sx={{ ml: 1 }}>
          {expired
            ? 'Code expired'
            : `${String(Math.floor(remaining / 60000)).padStart(2, '0')}:${String(
                Math.floor((remaining % 60000) / 1000)
              ).padStart(2, '0')}`}
        </Typography>
      </Button>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center' }}>
        Didn't receive a code? Check your spam folder or{' '}
        <strong
          style={{ cursor: 'pointer', color: '#1976d2' }}
          onClick={() => {
            onBack();
          }}
        >
          resend the code
        </strong>
        .
      </Typography>
    </form>
  );
}
